import { create } from "zustand";
import { applyEdits, getExportJob, getStatement, getUploadJob, resetEdits, startExportJob, uploadStatement } from "../api/engine";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const DASH_TEXT_RE = /^[-–—]{1,2}$/;

function extractedText(tx, field) {
    const key = field === "debit" ? "debit_extracted_text" : "credit_extracted_text";
    const editedDisplayKey = field === "debit" ? "edited_debit_display_text" : "edited_credit_display_text";
    if (tx[editedDisplayKey] != null) {
        return tx[editedDisplayKey];
    }
    return tx[key] ?? "";
}

function isDashText(text) {
    return DASH_TEXT_RE.test((text || "").trim());
}

function isExplicitZeroText(text) {
    const raw = (text || "").trim();
    if (!raw || isDashText(raw)) {
        return false;
    }
    const core = raw.replace(/,/g, "").replace(/^\((.*)\)$/, "$1").replace(/^-/, "");
    const num = Number(core);
    return Number.isFinite(num) && Math.abs(num) < 0.005;
}

function emptyPlaceholderText(tx, field) {
    const key = field === "debit" ? "debit_empty_placeholder" : "credit_empty_placeholder";
    if (tx[key] !== undefined && tx[key] !== null) {
        return tx[key];
    }
    const spanText = field === "debit" ? tx.debit_span?.text : tx.credit_span?.text;
    if ((spanText || "").trim()) {
        if (isDashText(spanText)) {
            return spanText;
        }
        if (isExplicitZeroText(spanText)) {
            return spanText;
        }
    }
    if (field === "debit" ? (tx.debit_dash || tx.debit_zero_dash) : (tx.credit_dash || tx.credit_zero_dash)) {
        return (spanText || "").trim() ? spanText : "";
    }
    return "";
}

function partnerEmptyPlaceholder(tx, field) {
    return emptyPlaceholderText(tx, field === "debit" ? "credit" : "debit");
}

function dashColumnText(tx, field) {
    return emptyPlaceholderText(tx, field);
}

function interchangeEmptyPlaceholder(tx, field) {
    const partner = field === "debit" ? "credit" : "debit";
    const partnerKey = partner === "debit" ? "debit_empty_placeholder" : "credit_empty_placeholder";
    if (tx[partnerKey] !== undefined && tx[partnerKey] !== null) {
        return tx[partnerKey];
    }
    return emptyPlaceholderText(tx, partner);
}

function partnerHasInterchangedAmount(tx, field, staged = {}) {
    const partner = field === "debit" ? "credit" : "debit";
    const partnerState = resolveColumnState(tx, partner, staged);
    return !partnerState.empty && !partnerState.dash;
}

function columnDisplayText(tx, field, staged = {}) {
    const stagedDisplayKey = `${field}_display_text`;
    if (staged[stagedDisplayKey] !== undefined) {
        return staged[stagedDisplayKey];
    }
    if (staged[`${field}_cleared`]) {
        return "";
    }
    if (staged[field] !== undefined) {
        if (staged[`${field}_zero_dash`]) {
            return extractedText(tx, field) || emptyPlaceholderText(tx, field);
        }
        return extractedText(tx, field);
    }
    const clearedKey = field === "debit" ? "edited_debit_cleared" : "edited_credit_cleared";
    const editedKey = field === "debit" ? "edited_debit" : "edited_credit";
    const dashKey = field === "debit" ? "edited_debit_zero_dash" : "edited_credit_zero_dash";
    const dashFlag = field === "debit"
        ? (tx.debit_dash || tx.debit_zero_dash)
        : (tx.credit_dash || tx.credit_zero_dash);
    if (tx[clearedKey]) {
        if (partnerHasInterchangedAmount(tx, field, staged)) {
            return interchangeEmptyPlaceholder(tx, field);
        }
        return "";
    }
    if (tx[editedKey] != null) {
        if (tx[dashKey]) {
            return extractedText(tx, field) || emptyPlaceholderText(tx, field);
        }
        return extractedText(tx, field);
    }
    let text = extractedText(tx, field);
    if (!(text || "").trim() && dashFlag) {
        text = dashColumnText(tx, field);
    }
    return text;
}

/** Current debit/credit cell value before flush — empty vs dash vs zero vs amount. */
export function resolveColumnState(tx, field, staged = {}) {
    const clearedKey = field === "debit" ? "edited_debit_cleared" : "edited_credit_cleared";
    const editedKey = field === "debit" ? "edited_debit" : "edited_credit";
    const dashKey = field === "debit" ? "edited_debit_zero_dash" : "edited_credit_zero_dash";
    const origDashKey = field === "debit" ? "debit_zero_dash" : "credit_zero_dash";
    const stagedDashKey = `${field}_zero_dash`;
    const text = columnDisplayText(tx, field, staged);
    const trimmed = (text || "").trim();

    if (staged[`${field}_cleared`]) {
        return { empty: true, value: null, dash: false, text: "" };
    }
    if (staged[field] !== undefined) {
        return {
            empty: false,
            value: staged[field],
            dash: Boolean(staged[stagedDashKey]),
            text,
        };
    }
    if (tx[clearedKey]) {
        return { empty: true, value: null, dash: false, text: "" };
    }
    if (tx[editedKey] != null) {
        return {
            empty: false,
            value: tx[editedKey],
            dash: Boolean(tx[dashKey]),
            text,
        };
    }
    if (!trimmed) {
        const dashFlag = field === "debit"
            ? (tx.debit_dash || tx.debit_zero_dash)
            : (tx.credit_dash || tx.credit_zero_dash);
        if (dashFlag) {
            return { empty: false, value: 0, dash: true, text: dashColumnText(tx, field) };
        }
        return { empty: true, value: null, dash: false, text: "" };
    }
    if (isDashText(trimmed)) {
        return { empty: false, value: 0, dash: true, text };
    }
    if (isExplicitZeroText(trimmed)) {
        return { empty: false, value: 0, dash: false, text };
    }
    const dashOrigKey = field === "debit" ? "debit_dash" : "credit_dash";
    if (tx[field] != null) {
        return {
            empty: false,
            value: tx[field],
            dash: Boolean(tx[origDashKey] || tx[dashOrigKey]),
            text,
        };
    }
    return { empty: true, value: null, dash: false, text: "" };
}

/** Build debit/credit interchange payload using raw PDF cell text. */
export function buildSwappedAmountEdit(tx, staged = {}, usesDashPlaceholders = false) {
    const debit = resolveColumnState(tx, "debit", staged);
    const credit = resolveColumnState(tx, "credit", staged);
    if (!usesDashPlaceholders) {
        return {
            swap_amounts: true,
            debit_display_text: credit.text,
            credit_display_text: debit.text,
        };
    }
    const debitHasAmount = !debit.empty && !debit.dash;
    const creditHasAmount = !credit.empty && !credit.dash;
    const debitText = credit.dash
        ? (credit.text || partnerEmptyPlaceholder(tx, "credit"))
        : credit.empty && debitHasAmount
          ? partnerEmptyPlaceholder(tx, "debit")
          : credit.text;
    const creditText = debit.dash
        ? (debit.text || partnerEmptyPlaceholder(tx, "debit"))
        : debit.empty && creditHasAmount
          ? partnerEmptyPlaceholder(tx, "credit")
          : debit.text;
    return {
        swap_amounts: true,
        debit_display_text: debitText,
        credit_display_text: creditText,
    };
}

export const useStatementStore = create((set, get) => ({
    statement: null,
    loading: false,
    error: null,
    uploadMessage: null,
    uploadProgress: 0,
    exportMessage: null,
    exportProgress: 0,
    exporting: false,
    pendingEdits: {},
    pendingTextEdits: {},
    pendingFreeTextEdits: {},
    pendingMetadataOverride: {},

    flushing: false,

    async upload(file, password = null) {
        set({ loading: true, error: null, uploadMessage: "Uploading PDF...", uploadProgress: 0 });
        try {
            let data = await uploadStatement(file, password);
            if (data?.status && data?.id && !data?.transactions) {
                set({
                    uploadMessage: data.message || "Processing PDF...",
                    uploadProgress: data.progress ?? 0,
                });
                for (let attempt = 0; attempt < 360; attempt += 1) {
                    await sleep(2000);
                    const job = await getUploadJob(data.id);
                    set({
                        uploadMessage: job.message || "Processing PDF...",
                        uploadProgress: job.progress ?? 0,
                    });
                    if (job.status === "done") {
                        data = job.statement;
                        break;
                    }
                    if (job.status === "error") {
                        throw new Error(job.message || "Failed to parse PDF");
                    }
                }
                if (!data?.transactions) {
                    throw new Error("Parsing is taking longer than expected. Please try again in a few minutes.");
                }
            }
            set({ statement: data, loading: false, uploadMessage: null, uploadProgress: 100, pendingEdits: {}, pendingTextEdits: {}, pendingFreeTextEdits: {}, pendingMetadataOverride: {} });
            return data;
        } catch (e) {
            const detail = e?.response?.data?.detail || e.message;
            // Locked PDF: don't surface this as a hard error — let the UI prompt
            // for a password and retry the upload with it.
            if (e?.response?.data?.requires_password) {
                set({ loading: false, uploadMessage: null, uploadProgress: 0 });
                const err = new Error(detail);
                err.requiresPassword = true;
                throw err;
            }
            set({ error: detail, loading: false, uploadMessage: null, uploadProgress: 0 });
            throw new Error(detail);
        }
    },

    async load(id) {
        set({ loading: true, error: null });
        try {
            const data = await getStatement(id);
            set({ statement: data, loading: false, uploadMessage: null, uploadProgress: 0, pendingEdits: {}, pendingTextEdits: {}, pendingFreeTextEdits: {}, pendingMetadataOverride: {} });
            return data;
        } catch (e) {
            set({ error: e.message, loading: false });
        }
    },

    stageEdit(txId, field, value, options = {}) {
        const { statement, pendingEdits: prev } = get();
        const pendingEdits = { ...prev };
        const current = { ...(pendingEdits[txId] || {}) };
        const tx = statement?.transactions?.find((t) => t.id === txId);
        const originalField = field === "debit" ? tx?.debit : tx?.credit;
        const dashKey = `${field}_zero_dash`;

        if (value === "" || value === null || Number.isNaN(value)) {
            if (originalField != null) {
                current[field] = 0;
            } else {
                delete current[field];
            }
            delete current[dashKey];
            delete current[`${field}_cleared`];
        } else {
            current[field] = value;
            delete current[`${field}_cleared`];
            if (options.zeroDash) {
                current[dashKey] = true;
            } else {
                delete current[dashKey];
            }
        }
        if (Object.keys(current).length === 0) {
            delete pendingEdits[txId];
        } else {
            pendingEdits[txId] = current;
        }
        set({ pendingEdits });
    },

    swapAmounts(txId) {
        const { statement, pendingEdits: prev } = get();
        const tx = statement?.transactions?.find((t) => t.id === txId);
        if (!tx) return;
        const staged = prev[txId] || {};
        const debit = resolveColumnState(tx, "debit", staged);
        const credit = resolveColumnState(tx, "credit", staged);
        if (debit.empty && credit.empty && !debit.dash && !credit.dash) return;
        const pendingEdits = {
            ...prev,
            [txId]: buildSwappedAmountEdit(
                tx,
                staged,
                Boolean(statement?.uses_dash_amount_placeholders),
            ),
        };
        set({ pendingEdits });
    },

    stageTextEdit(txId, field, value) {
        const pendingTextEdits = { ...get().pendingTextEdits };
        const current = { ...(pendingTextEdits[txId] || {}) };
        if (value === null || value === undefined) {
            delete current[field];
        } else {
            current[field] = value;
        }
        if (Object.keys(current).length === 0) {
            delete pendingTextEdits[txId];
        } else {
            pendingTextEdits[txId] = current;
        }
        set({ pendingTextEdits });
    },

    /** Stage a free-text span edit (name, address, header, etc.) */
    stageFreeTextEdit(spanId, newText) {
        const pendingFreeTextEdits = { ...get().pendingFreeTextEdits };
        if (newText === null || newText === undefined) {
            delete pendingFreeTextEdits[spanId];
        } else {
            pendingFreeTextEdits[spanId] = newText;
        }
        set({ pendingFreeTextEdits });
    },
    setMetadataOverride(key, value) {
        const { pendingMetadataOverride } = get();
        set({ pendingMetadataOverride: { ...pendingMetadataOverride, [key]: value } });
    },

    async flushEdits() {
        if (get()._flushInFlight) {
            get()._needsFlushAgain = true;
            return;
        }
        const { statement, pendingEdits, pendingTextEdits, pendingFreeTextEdits, pendingMetadataOverride } = get();
        if (!statement) return;
        const edits = Object.entries(pendingEdits).map(([transaction_id, vals]) => ({
            transaction_id,
            debit: vals.debit !== undefined ? vals.debit : null,
            credit: vals.credit !== undefined ? vals.credit : null,
            debit_cleared: Boolean(vals.debit_cleared),
            credit_cleared: Boolean(vals.credit_cleared),
            debit_zero_dash: Boolean(vals.debit_zero_dash),
            credit_zero_dash: Boolean(vals.credit_zero_dash),
            debit_display_text: vals.debit_display_text ?? null,
            credit_display_text: vals.credit_display_text ?? null,
            swap_amounts: Boolean(vals.swap_amounts),
        }));
        const text_edits = Object.entries(pendingTextEdits).map(([transaction_id, vals]) => ({
            transaction_id,
            description: vals.description ?? null,
            reference: vals.reference ?? null,
            date: vals.date ?? null,
        }));
        const free_text_edits = Object.entries(pendingFreeTextEdits).map(([span_id, new_text]) => ({
            span_id,
            new_text,
        }));
        if (edits.length === 0 && text_edits.length === 0 && free_text_edits.length === 0 && Object.keys(pendingMetadataOverride).length === 0) return;
        set({ flushing: true, _flushInFlight: true });
        try {
            const data = await applyEdits(statement.id, edits, text_edits, free_text_edits, Object.keys(pendingMetadataOverride).length > 0 ? pendingMetadataOverride : null);
            set({ statement: data, pendingEdits: {}, pendingTextEdits: {}, pendingFreeTextEdits: {}, pendingMetadataOverride: {}, flushing: false, _flushInFlight: false });
        } catch (e) {
            set({ error: e.message, flushing: false, _flushInFlight: false });
        }
        if (get()._needsFlushAgain) {
            get()._needsFlushAgain = false;
            await get().flushEdits();
        }
    },

    async resetAll() {
        const { statement } = get();
        if (!statement) return;
        set({ flushing: true });
        try {
            const data = await resetEdits(statement.id);
            set({ statement: data, pendingEdits: {}, pendingTextEdits: {}, pendingFreeTextEdits: {}, pendingMetadataOverride: {}, flushing: false });
        } catch (e) {
            set({ error: e.message, flushing: false });
        }
    },

    async prepareExport() {
        const { statement } = get();
        if (!statement) return null;
        await get().flushEdits();
        set({ exporting: true, exportMessage: "Preparing export...", exportProgress: 0, error: null });
        try {
            let job = await startExportJob(statement.id);
            set({
                exportMessage: job.message || "Preparing export...",
                exportProgress: job.progress ?? 0,
            });
            for (let attempt = 0; attempt < 240 && job.status !== "done"; attempt += 1) {
                if (job.status === "error") {
                    throw new Error(job.message || "Export failed");
                }
                await sleep(1000);
                job = await getExportJob(job.id);
                set({
                    exportMessage: job.message || "Preparing export...",
                    exportProgress: job.progress ?? 0,
                });
            }
            if (job.status !== "done") {
                throw new Error("Export is taking longer than expected. Please try again.");
            }
            set({ exporting: false, exportMessage: null, exportProgress: 100 });
            return statement.id;
        } catch (e) {
            set({
                exporting: false,
                exportMessage: null,
                exportProgress: 0,
                error: e.message,
            });
            throw e;
        }
    },

    clear() {
        set({ statement: null, pendingEdits: {}, pendingTextEdits: {}, pendingFreeTextEdits: {}, pendingMetadataOverride: {}, error: null, uploadMessage: null, uploadProgress: 0, exportMessage: null, exportProgress: 0, exporting: false });
    },
}));
